import React, { useState, useEffect } from "react";
import { Controller } from "react-hook-form";
import { CoreLocationWithAllIncludes, getLocation, postLocationSearch } from "../../../queries/locations";
import { Accordion, Button, Form, Header, Icon, Label, Loader, Segment } from "semantic-ui-react";
import { useDebounce } from "../../../hooks/useDebounce";
import { useQuery } from "react-query";
import { fieldError, makeFullAddress } from "../../../utils/functions";
import { useToasts } from "react-toast-notifications";
import { PropertyGrid } from "../PropertyGrid";
import { FirstArgumentType } from "../../../types/ArgumentTypes";
import { LocationGridRow } from "../../../features/locations/utils/LocationGridRow";
import { FullAddress } from "../../../utils/FullAddress";
import { useId } from "react-aria";
import ReactSelect, { OptionProps } from "react-select";
import decamelize from "decamelize-keys-deep";
import camelcaseKeysDeep from "camelcase-keys-deep";
import useCountries from "../../../hooks/useCountries";
import PlaceholderUntilLoaded from "../PlaceholderUntilLoaded";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";
import "./ControlledLocation.scss";

function CustomDropdownIndicator({ selectProps }: any) {
  if (selectProps.isLoading) return <Loader style={{ width: "12px", height: "18px" }} active inline />;
  return <i style={{ width: "12px", height: "18px" }} className='icon search'></i>;
}

function CustomOption({
  data,
  innerRef,
  innerProps,
  isFocused,
}: OptionProps<{
  location: {
    parcel_number: string;
    location_address: {
      address: string;
      address2: string;
      city: string;
      state: string;
      zip: string;
      country_id: string;
    } | null;
  };
}>) {
  if (!data.location.location_address) {
    return (
      <div ref={innerRef} {...innerProps} className={"option" + (isFocused ? " focused" : "")}>
        <div className='header'>{`Parcel # ${data.location.parcel_number}`}</div>
        <div className='header sub'>Address not assigned</div>
      </div>
    );
  }

  const { address, address2, city, state, zip, country_id } = data.location.location_address;
  return (
    <div ref={innerRef} {...innerProps} className={"option" + (isFocused ? " focused" : "")}>
      <div className='header'>{makeFullAddress(address, address2)}</div>
      <div className='header sub'>{makeFullAddress(null, null, city, state, zip, null, country_id)}</div>
    </div>
  );
}

interface IControlledLocation {
  control: any;
  name: string;
  defaultValue?: CoreLocationWithAllIncludes | null;
  readonly?: boolean;
  label?: string;
  subLabel?: string;
  errors?: any;
  required?: boolean;
  rules?: any;
  hideErrorMessage?: boolean;
  isPublicView?: boolean;
  showEmptyFields?: boolean;
  id?: string;
}

export default function ControlledLocation({
  control,
  name,
  defaultValue = null,
  readonly = false,
  label,
  subLabel,
  errors,
  required,
  rules,
  hideErrorMessage = false,
  isPublicView = false,
  showEmptyFields = true,
  id: inputId,
}: IControlledLocation) {
  const isError = fieldError(errors, name);
  const id = inputId ?? useId();

  const [selectedLocationSearchResult, setSelectedLocationsSearchResult] = useState<any>(null);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 350);

  const networkHeaders = useNetworkHeaders();
  const { addToast } = useToasts();
  const { agencyCountry } = useCountries();

  // Handles updating the selected location result if a default value is given - this triggers the units-options to load
  useEffect(() => {
    if (defaultValue) {
      setSelectedLocationsSearchResult(decamelize(defaultValue, "_"));
    }
  }, [defaultValue]);

  const { data: locationSearchResults, isLoading: isLocationSearchResultsLoading } = useQuery(
    ["Locations", "Search", { query: debouncedQuery }],
    {
      queryFn: postLocationSearch(networkHeaders),
      queryFnParamsFilter: (args) => {
        const { query }: { query: string } = args[2] as any;

        const searchParams: FirstArgumentType<ReturnType<typeof postLocationSearch>> = {
          query,
          queryFields: [
            "location_name",
            "location_address.address",
            "location_address.address2",
            "location_address.city",
            "location_address.state",
            "location_address.zip",
            "parcel_number",
          ],
          take: 5,
          includeDeactivated: false,
        };

        return [searchParams];
      },
      enabled: debouncedQuery !== "",
    },
  );

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      rules={{ required: required && "This field is required", ...rules }}
      render={({ onChange, value }) => {
        const location: CoreLocationWithAllIncludes | null = camelcaseKeysDeep(value) as any;
        const [isLocationLoading, setIsLocationLoading] = useState(false);
        const [readonlyLocationAccordionActive, setReadonlyLocationAccordionActive] = useState(false);

        const showFlag = !!agencyCountry && location?.locationAddress?.countryId !== agencyCountry?.id; // Only display flag if country differs from agency country
        const handleChangeLocation = async (locationSearchResult: any) => {
          setIsLocationLoading(true);
          setSelectedLocationsSearchResult(locationSearchResult);
          try {
            const location = await getLocation(networkHeaders)(locationSearchResult.id);
            onChange(location);
          } catch (e: any) {
            addToast(e?.error?.message ?? "Something went wrong", { appearance: "error", autoDismiss: true });
            setSelectedLocationsSearchResult(null);
            onChange(null);
          }
          setIsLocationLoading(false);
        };

        // Grab list of other locations with different unit numbers when we select a search result
        const { data: locationOtherUnits, isLoading: isLocationOtherUnitsLoading } = useQuery(
          [
            "Locations",
            "Search",
            {
              address: selectedLocationSearchResult?.location_address?.address,
              city: selectedLocationSearchResult?.location_address?.city,
              state: selectedLocationSearchResult?.location_address?.state,
              zip: selectedLocationSearchResult?.location_address?.zip,
            },
          ],
          {
            queryFn: postLocationSearch(networkHeaders),
            queryFnParamsFilter: (args) => {
              const { address, city, state, zip }: { address: string; city: string; state: string; zip: string } =
                args[2] as any;

              const searchParams: FirstArgumentType<ReturnType<typeof postLocationSearch>> = {
                filters: [
                  {
                    field: "location_address.address.keyword",
                    value: address,
                  },
                  {
                    field: "location_address.city.keyword",
                    value: city,
                  },
                  {
                    field: "location_address.state.keyword",
                    value: state,
                  },
                  {
                    field: "location_address.zip.keyword",
                    value: zip,
                  },
                ],
                take: 100,
                includeDeactivated: false,
              };

              return [searchParams];
            },
            enabled: selectedLocationSearchResult,
          },
        );

        const unitDropdownOptions = locationOtherUnits?.hits?.hits?.map((locationSearchResult: any) => {
          if (!locationSearchResult._source.location_address?.address2)
            return {
              key: locationSearchResult._source.id,
              value: locationSearchResult._source.id,
              text: "No Unit",
            };
          return {
            key: locationSearchResult._source.id,
            value: locationSearchResult._source.id,
            text: locationSearchResult._source.location_address.address2,
          };
        });

        if (readonly) {
          return (
            <div className='controlled-location-readonly'>
              <Segment className={isPublicView ? "hover-effect" : ""}>
                <Accordion fluid>
                  <Accordion.Title
                    active={readonlyLocationAccordionActive}
                    tabIndex={0}
                    index={0}
                    onKeyDown={(e: any) => {
                      if (e.key === "Enter") {
                        setReadonlyLocationAccordionActive((prev) => !prev);
                      }
                    }}
                    onClick={() => {
                      // TODO: this should be a link to the location page on permits details
                      if (!isPublicView) return;
                      setReadonlyLocationAccordionActive((prev) => !prev);
                    }}
                  >
                    <div className='flex justify-between w-full'>
                      <div className='flex items-center gap-8'>
                        {isPublicView && (
                          <Button
                            icon='dropdown'
                            size='large'
                            className='icon-only-button'
                            aria-expanded={readonlyLocationAccordionActive}
                            aria-label='Primary Location'
                          />
                        )}
                        <div className='header-container'>
                          {!location?.locationAddress && (
                            <div>
                              <div className='header'>{`Parcel # ${location?.parcelNumber}`}</div>
                              <div className='header sub'>Address not assigned</div>
                            </div>
                          )}
                          {location?.locationAddress && (
                            <div>
                              <div className='header'>
                                {makeFullAddress(
                                  location?.locationAddress?.address,
                                  location?.locationAddress?.address2,
                                )}
                              </div>
                              <div className='header sub'>
                                <FullAddress
                                  city={location?.locationAddress?.city}
                                  state={location?.locationAddress?.state}
                                  zip={location?.locationAddress?.zip}
                                  countryId={showFlag ? location?.locationAddress?.countryId : null}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <Icon className='building-icon' name='building outline' inverted circular size='large' />
                    </div>
                  </Accordion.Title>
                  <Accordion.Content active={readonlyLocationAccordionActive}>
                    <div className='flex flex-col gap-8 content-container'>
                      <Header.Subheader className='small-caption'>Property Data</Header.Subheader>
                      <PropertyGrid>
                        <LocationGridRow
                          label='Parcel #'
                          value={location?.parcelNumber}
                          valueType='Text'
                          showWhenEmpty={showEmptyFields}
                        />
                        <LocationGridRow
                          label='Lot Size'
                          value={location?.lotSize}
                          valueType='Number'
                          showWhenEmpty={showEmptyFields}
                        />
                        <LocationGridRow
                          label='Building Type'
                          value={location?.buildingType?.value}
                          valueType='Dropdown'
                          showWhenEmpty={showEmptyFields}
                        />
                        <LocationGridRow
                          label='Year Built'
                          value={location?.yearBuilt}
                          valueType='Number'
                          showWhenEmpty={showEmptyFields}
                        />
                        <LocationGridRow
                          label='Zoning'
                          value={location?.zoning}
                          valueType='Text'
                          showWhenEmpty={showEmptyFields}
                        />
                      </PropertyGrid>
                    </div>
                  </Accordion.Content>
                </Accordion>
              </Segment>
            </div>
          );
        }

        return (
          <>
            {label && <label htmlFor={!readonly ? id : undefined}>{label}</label>}
            {subLabel && <div className='controlled-location-sub-label'>{subLabel}</div>}
            <div className='controlled-location'>
              {!selectedLocationSearchResult && !location && (
                <ReactSelect
                  inputId={id}
                  unstyled
                  components={{
                    DropdownIndicator: CustomDropdownIndicator,
                    LoadingIndicator: () => null,
                    Option: CustomOption,
                  }}
                  classNames={{
                    multiValue: () => "ui label circular",
                  }}
                  styles={{
                    control: (base, props) => ({
                      ...base,
                      "display": "flex",
                      "padding": "0 12px 0 16px",
                      "paddingLeft": props.isMulti && props.hasValue ? "4px" : "16px",
                      "color": "rgba(0, 0, 0, 0.87)",
                      "border": "1px solid rgba(34, 36, 38, 0.15)",
                      "backgroundColor": isError ? "#FFF6F6" : undefined,
                      "borderRadius": props.menuIsOpen ? "0.28571429rem 0.28571429rem 0 0" : "0.28571429rem",
                      "borderColor": isError ? "#E0B4B4" : props.menuIsOpen ? "#96C8DA" : undefined,
                      "boxShadow": props.menuIsOpen ? "0px 2px 3px 0px rgba(34, 36, 38, 0.15)" : undefined,
                      "borderBottomColor": props.menuIsOpen ? "rgba(0, 0, 0, 0)" : undefined,
                      "transition": "none",
                      ":hover": {
                        cursor: "text",
                      },
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      color: "rgba(0, 1, 0, 0.87)",
                    }),
                    indicatorsContainer: (base) => ({
                      ...base,
                      "gap": "2px",
                      "color": "rgba(0, 1, 0, 0.87)",
                      ":hover": {
                        cursor: "pointer",
                      },
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: "white",
                      borderLeft: "1px solid rgba(34, 36, 38, 0.15)",
                      borderRight: "1px solid rgba(34, 36, 38, 0.15)",
                      borderBottom: "1px solid rgba(34, 36, 38, 0.15)",
                      borderColor: "#96C8DA",
                      borderRadius: "0 0 0.28571429rem 0.28571429rem",
                      boxShadow: "0px 2px 3px 0px rgba(34, 36, 38, 0.15)",
                      marginTop: "-1px",
                      zIndex: 3,
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "rgba(191, 191, 191, 0.87)",
                    }),
                    noOptionsMessage: (base) => ({
                      ...base,
                      padding: "8px 16px",
                      borderTop: "1px solid #FAFAFA",
                    }),
                    loadingMessage: (base) => ({
                      ...base,
                      padding: "8px 16px",
                      borderTop: "1px solid #FAFAFA",
                    }),
                  }}
                  onChange={async (value) => {
                    await handleChangeLocation((value as any)?.location);
                  }}
                  onInputChange={(value) => {
                    setQuery(value);
                  }}
                  name={name}
                  isLoading={isLocationSearchResultsLoading}
                  noOptionsMessage={() => {
                    return debouncedQuery === "" ? <div>Start typing to search</div> : <div>No Results</div>;
                  }}
                  options={locationSearchResults?.hits?.hits.map(({ _source: location }: { _source: any }) => {
                    return {
                      location,
                    };
                  })}
                  filterOption={() => true}
                  placeholder=''
                  ariaLiveMessages={{
                    // Notify assistive tech of which option is currently selected
                    onFocus: ({ focused, isDisabled }) => {
                      const {
                        address = "",
                        address2 = "",
                        city = "",
                        state = "",
                      } = focused.location.location_address || {};
                      const fullAddress = [address, address2, city, state].filter((str) => str).join(", ");

                      return `Focused on ${fullAddress}${isDisabled ? ", disabled" : ""}`;
                    },
                  }}
                />
              )}
              <>
                {isLocationLoading && (
                  <Segment>
                    <PlaceholderUntilLoaded isLoading lineCount={2} />
                    <PlaceholderUntilLoaded isLoading lineCount={1} />
                    <PlaceholderUntilLoaded isLoading lineCount={5} />
                  </Segment>
                )}
                {!isLocationLoading && location && (
                  <Segment className={"location-selected-segment" + (isError ? " error" : "")}>
                    <div className='location-selected-header-container'>
                      {!location.locationAddress && (
                        <div>
                          <div className='header'>{`Parcel # ${location.parcelNumber}`}</div>
                          <div className='header sub'>Address not assigned</div>
                        </div>
                      )}
                      {location.locationAddress && (
                        <div>
                          <div className='header'>
                            {makeFullAddress(location?.locationAddress?.address, location?.locationAddress?.address2)}
                          </div>
                          <div className='header sub'>
                            {makeFullAddress(
                              null,
                              null,
                              location?.locationAddress?.city,
                              location?.locationAddress?.state,
                              location?.locationAddress?.zip,
                              null,
                              location?.locationAddress?.countryId,
                            )}
                          </div>
                        </div>
                      )}
                      <Button
                        secondary
                        type='button'
                        content='Change Location'
                        onClick={async () => {
                          setSelectedLocationsSearchResult(null);
                          onChange(null);
                        }}
                      />
                    </div>
                    {location.locationAddress && location.locationAddress.address2 && (
                      <Form as='div'>
                        <Form.Field>
                          <label htmlFor='unit'>Unit</label>
                          <div className='custom-select-wrapper'>
                            {/* Disabling eslint error for no onChange, as it does not change context, so it is not a wcag issue */}
                            {/* eslint-disable-next-line jsx-a11y/no-onchange */}
                            <select
                              value={location.id}
                              disabled={isLocationOtherUnitsLoading}
                              onChange={async (e) => {
                                const selectedLocationsSearchResult = locationOtherUnits?.hits?.hits
                                  ?.map((locationSearchResult: { _source: any }) => locationSearchResult._source)
                                  ?.find((locationSearchResult: { id: number }) => {
                                    return locationSearchResult.id.toString() === e.target.value;
                                  });
                                if (!selectedLocationsSearchResult) return;
                                await handleChangeLocation(selectedLocationsSearchResult);
                              }}
                            >
                              {unitDropdownOptions?.map((option: any) => (
                                <option key={option.key} value={option.value}>
                                  {option.text}
                                </option>
                              ))}
                            </select>
                            <Icon name='caret down' />
                          </div>
                        </Form.Field>
                      </Form>
                    )}
                    <PropertyGrid error={!!isError}>
                      <LocationGridRow
                        label='Parcel #'
                        value={location.parcelNumber}
                        valueType='Text'
                        showWhenEmpty={showEmptyFields}
                      />
                      <LocationGridRow
                        label='Lot Size'
                        value={location.lotSize}
                        valueType='Number'
                        showWhenEmpty={showEmptyFields}
                      />
                      <LocationGridRow
                        label='Building Type'
                        value={location.buildingType?.value}
                        valueType='Dropdown'
                        showWhenEmpty={showEmptyFields}
                      />
                      <LocationGridRow
                        label='Year Built'
                        value={location.yearBuilt}
                        valueType='Number'
                        showWhenEmpty={showEmptyFields}
                      />
                      <LocationGridRow
                        label='Zoning'
                        value={location.zoning}
                        valueType='Text'
                        showWhenEmpty={showEmptyFields}
                      />
                    </PropertyGrid>
                  </Segment>
                )}
              </>
            </div>
            {isError && !hideErrorMessage && (
              <Label basic prompt pointing='above'>
                {isError?.content.message}
              </Label>
            )}
          </>
        );
      }}
    />
  );
}
